import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Edit3, Trash2 } from 'lucide-react';
import { ADMIN } from '@/constants/testIds';
import ImageUploader from '@/components/admin/ImageUploader';

/**
 * Reusable admin CRUD manager.
 * Props:
 *  - title, subtitle
 *  - endpoint: API path (e.g. '/admin/products')
 *  - listEndpoint: optional override for GET
 *  - idField: the identifier field on the resource (e.g. 'product_id', 'slug', 'code')
 *  - columns: [{ key, label, render? }]
 *  - fields: [{ key, label, type, options?, placeholder?, help?, rows? }]
 *  - initial: default values for new record
 *  - useKeyInPath: if true, the identifier is used in the URL path (e.g. articles by slug)
 *  - deleteMethod: 'DELETE' default
 *  - rowExtraActions: optional (item) => JSX rendered before the edit/delete icons on each row
 */
const CrudManager = ({ title, subtitle, endpoint, listEndpoint, idField, columns, fields, initial = {}, useKeyInPath = false, rowExtraActions = null }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(initial);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(listEndpoint || endpoint);
      setItems(data);
    } catch (e) {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [endpoint]);

  const openNew = () => { setForm(initial); setEditing('new'); };
  const openEdit = (item) => { setForm({ ...initial, ...item }); setEditing(item[idField]); };
  const closeModal = () => { setEditing(null); setForm(initial); };

  const parseVal = (fld, v) => {
    if (fld.type === 'number') return v === '' ? '' : Number(v);
    if (fld.type === 'array') return typeof v === 'string' ? v.split('\n').map((s) => s.trim()).filter(Boolean) : v;
    if (fld.type === 'checkbox') return !!v;
    return v;
  };

  const save = async () => {
    // Convert array fields
    const payload = { ...form };
    for (const f of fields) {
      if (f.type === 'array' && typeof payload[f.key] === 'string') {
        payload[f.key] = payload[f.key].split('\n').map((s) => s.trim()).filter(Boolean);
      }
      if (f.type === 'json' && typeof payload[f.key] === 'string') {
        try { payload[f.key] = JSON.parse(payload[f.key] || '{}'); } catch { toast.error(`Invalid JSON in ${f.label}`); return; }
      }
      if (f.type === 'number' && typeof payload[f.key] === 'string') {
        payload[f.key] = payload[f.key] === '' ? 0 : Number(payload[f.key]);
      }
    }
    try {
      if (editing === 'new') {
        await api.post(endpoint, payload);
        toast.success('Created');
      } else {
        const key = useKeyInPath ? payload[idField] : editing;
        await api.put(`${endpoint}/${key}`, payload);
        toast.success('Updated');
      }
      closeModal();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (item) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await api.delete(`${endpoint}/${item[idField]}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const renderField = (f) => {
    const val = form[f.key];
    const display = f.type === 'array' && Array.isArray(val) ? val.join('\n') : (f.type === 'json' && typeof val === 'object' ? JSON.stringify(val || {}, null, 2) : (val ?? ''));
    if (f.type === 'select') {
      return (
        <select value={display} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="w-full border border-sans_line bg-white px-3 py-2.5 text-sm text-sans_ink focus:outline-none focus:border-sans_ink">
          {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    if (f.type === 'textarea' || f.type === 'array' || f.type === 'json') {
      return (
        <textarea rows={f.rows || 4} value={display} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder} className="w-full border border-sans_line bg-white px-3 py-2.5 text-sm text-sans_ink focus:outline-none focus:border-sans_ink font-mono" />
      );
    }
    if (f.type === 'checkbox') {
      return (
        <label className="flex items-center gap-2 text-sm text-sans_ink">
          <input type="checkbox" checked={!!val} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} className="accent-sans_ink" />
          {f.checkboxLabel || 'Enabled'}
        </label>
      );
    }
    if (f.type === 'image') {
      return (
        <ImageUploader
          value={typeof val === 'string' ? val : ''}
          onChange={(v) => setForm({ ...form, [f.key]: v })}
        />
      );
    }
    if (f.type === 'image-array') {
      return (
        <ImageUploader
          multiple
          value={Array.isArray(val) ? val : (typeof val === 'string' && val ? val.split('\n').filter(Boolean) : [])}
          onChange={(v) => setForm({ ...form, [f.key]: v })}
        />
      );
    }
    return (
      <input type={f.type || 'text'} value={display} onChange={(e) => setForm({ ...form, [f.key]: parseVal(f, e.target.value) })} placeholder={f.placeholder} className="w-full border border-sans_line bg-white px-3 py-2.5 text-sm text-sans_ink focus:outline-none focus:border-sans_ink" />
    );
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-6 mb-8">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-sans_ink tracking-tight">{title}</h1>
          {subtitle && <p className="text-sm text-sans_mute mt-2">{subtitle}</p>}
        </div>
        <button onClick={openNew} data-testid={ADMIN.newBtn} className="sans-btn-primary text-xs">
          <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> New
        </button>
      </div>

      <div className="bg-white border border-sans_line overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-sans_line">
              {columns.map((c) => <th key={c.key} className="text-left px-4 py-3 font-medium text-[11px] tracking-[0.18em] uppercase text-sans_mute">{c.label}</th>)}
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-sans_mute">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-sans_mute">No entries yet.</td></tr>}
            {items.map((item) => (
              <tr key={item[idField]} className="border-b border-sans_line hover:bg-sans_bone/50">
                {columns.map((c) => <td key={c.key} className="px-4 py-3 text-sans_ink align-top">{c.render ? c.render(item) : (item[c.key] ?? '')}</td>)}
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {rowExtraActions && rowExtraActions(item)}
                  <button data-testid={ADMIN.editBtn(item[idField])} onClick={() => openEdit(item)} className="text-sans_mute hover:text-sans_ink mr-3" aria-label="Edit"><Edit3 className="w-4 h-4" strokeWidth={1.5} /></button>
                  <button data-testid={ADMIN.deleteBtn(item[idField])} onClick={() => remove(item)} className="text-sans_mute hover:text-red-600" aria-label="Delete"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && closeModal()}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-sans_ink">{editing === 'new' ? 'New' : 'Edit'} — {title.replace(/s$/, '')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4">
            {fields.map((f) => (
              <div key={f.key}>
                <label className="sans-label block mb-2">{f.label}{f.required && ' *'}</label>
                {renderField(f)}
                {f.help && <div className="mt-1 text-xs text-sans_mute">{f.help}</div>}
              </div>
            ))}
          </div>
          <DialogFooter>
            <button onClick={closeModal} className="sans-btn-secondary">Cancel</button>
            <button onClick={save} data-testid={ADMIN.saveBtn} className="sans-btn-primary">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrudManager;
